/*
 * File: api/index.js
 * Mô tả: Tập trung tất cả các hàm gọi API của frontend.
 * PHIÊN BẢN SỬA LỖI: Đã sửa các hàm gọi API mới để sử dụng customFetch và đồng bộ đường dẫn API.
 */

// Xác định URL của backend server
const API_URL = process.env.NODE_ENV === 'production'
    ? '' // Ở production, API được gọi trên cùng domain
    : 'http://localhost:5000'; // Ở development, chỉ định rõ backend

// --- Hàm fetch tùy chỉnh để tự động gửi cookie và xử lý lỗi ---
const customFetch = async (url, options = {}) => {
    options.credentials = 'include'; // Luôn gửi kèm cookie

    if (options.body && typeof options.body !== 'string' && !(options.body instanceof FormData)) {
        options.body = JSON.stringify(options.body);
        if (!options.headers) options.headers = {};
        options.headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}${url}`, options);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Lỗi ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        return response.json();
    }
    return { success: true };
};

// --- HELPER 2: Hàm fetch tùy chỉnh cho các API xuất file (trả về blob) ---
const apiFetchFile = async (url) => {
    console.log(`[API-HELPER-LOG] Requesting URL: ${API_URL}${url}`); // LOG F5
    try {
        const response = await fetch(`${API_URL}${url}`, { credentials: 'include' });

        if (response.ok) {
            console.log(`[API-HELPER-LOG] Response OK for ${url}. Returning blob.`); // LOG F6
            return response.blob();
        } else {
            const errorData = await response.json().catch(() => ({}));
            console.error(`[API-HELPER-ERROR] Response NOT OK for ${url}. Error data:`, errorData); // LOG F7
            throw new Error(errorData.message || `Lỗi ${response.status}: Không thể xuất file.`);
        }
    } catch (networkError) {
        console.error(`[API-HELPER-ERROR] Network error for ${url}:`, networkError); // LOG F8
        throw networkError;
    }
};

// --- Auth APIs ---
export const apiLogin = (empid, password) => customFetch(`/api/login`, { method: 'POST', body: { empid, password } });
export const apiLogout = () => customFetch(`/api/logout`, { method: 'POST' });
export const apiCheckSession = () => customFetch(`/api/check-session`);
export const apiChangePassword = (userId, oldPassword, newPassword) => customFetch(`/api/user/change-password`, { method: 'POST', body: { userId, oldPassword, newPassword } });

// --- User APIs ---
export const apiFetchTimesheet = (userId, yearMonth) => customFetch(`/api/timesheet/${userId}/${yearMonth}`);
export const apiFetchPayroll = (userId, yearMonth) => customFetch(`/api/payroll/${userId}/${yearMonth}`);
export const apiFetchHolidays = (userId, year) => customFetch(`/api/holidays/${userId}/${year}`);
export const apiFetchLuongT13 = (userId, year) => customFetch(`/api/luong-t13/${userId}/${year}`);

// --- Admin - Employee Management APIs ---
export const apiGetAllEmployees = () => customFetch('/api/admin/employee');
export const apiAddEmployee = (employeeData) => customFetch('/api/admin/employee', { method: 'POST', body: employeeData });
export const apiGetEmployeeInfo = (empid) => customFetch(`/api/admin/employee/${empid}/info`);
export const apiUpdateEmployee = (empid, data) => customFetch(`/api/admin/employee/${empid}`, { method: 'PUT', body: data });
export const apiDeleteEmployee = (empid, outDate) => customFetch(`/api/admin/employee/${empid}/resign`, { method: 'PUT', body: { outDate } });
export const apiAdminResetPassword = (empid) => customFetch('/api/admin/employee/reset-password', { method: 'POST', body: { empid } });
export const apiGetNextEmployeeId = () => customFetch('/api/admin/employee/next-id');
export const apiGetEmployeeInfoReset = (empid) => customFetch(`/api/admin/employee-info/${empid}`);

// --- Admin - Leave Management APIs ---
export const apiAdminSubmitLeave = (leaveData) => customFetch(`/api/admin/submit-leave`, { method: 'POST', body: leaveData });
export const apiGetLeaveEntries = (userId, year) => customFetch(`/api/admin/leave-entries/${userId}/${year}`);
export const apiDeleteLeaveEntry = (id) => customFetch(`/api/admin/leave-entry/${id}`, { method: 'DELETE' });
export const apiUpdateLeaveEntry = (id, updateData) => customFetch(`/api/admin/leave-entry/${id}`, { method: 'PUT', body: updateData });
export const apiGetLeaveSummaryByGroup = (year, groupId) => customFetch(`/api/admin/summary/${year}?groupId=${groupId || 'ALL'}`);
// === CÁC HÀM MỚI ĐÃ SỬA LỖI ===
export const apiResetEmployeeLeave = (resetData) => {
    // Sửa: Dùng customFetch và đường dẫn API đúng theo pattern /api/admin
    return customFetch('/api/admin/reset-employee', {
        method: 'POST',
        body: resetData,
    });
};
export const apiSetLeaveExpiry = (expiryData) => {
    // Sửa: Dùng customFetch và đường dẫn API đúng theo pattern /api/admin
    return customFetch('/api/admin/config/expiry-date', {
        method: 'POST',
        body: expiryData,
    });
};

// --- HÀM MỚI: CHỐT SỔ TOÀN BỘ ---
export const apiResetAllEmployees = (resetData) => {
    return customFetch('/api/admin/reset-all-employees', {
        method: 'POST',
        body: resetData,
    });
};

// --- Admin - Other APIs ---
export const apiFetchAllPayrolls = (yearMonth, groupId = 'ALL') => customFetch(`/api/admin/all-payrolls/${yearMonth}?groupId=${groupId}`);
export const apiApprovePayroll = (yearMonth) => customFetch(`/api/admin/approve-payroll`, { method: 'POST', body: { yearMonth } });
export const apiGetGroups = () => customFetch(`/api/admin/groups`);
export const apiGetBasicCodeOptions = (func) => customFetch(`/api/admin/basic-code/${func}`);
export const apiUploadLeaveFile = async (file) => {
    const formData = new FormData();
    formData.append('leaveFile', file);
    return customFetch(`/api/admin/upload-leave`, { method: 'POST', body: formData });
};
export const apiUploadLuongT13 = (file, year) => {
    const formData = new FormData();
    formData.append('luongT13File', file);
    formData.append('year', year);
    return customFetch(`/api/admin/upload-luong-t13`, { method: 'POST', body: formData });
};

// --- Admin Export APIs ---
export const apiExportLeaveFile = async (year, groupId = 'ALL') => {
    try {
        const response = await fetch(`${API_URL}/api/admin/export-leave/${year}?groupId=${groupId}`, { credentials: 'include' });
        if (!response.ok) {
             const errorData = await response.json().catch(() => ({ message: 'Xuất file thất bại.' }));
             throw new Error(errorData.message);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TongHopPhepNam_${year}${groupId && groupId !== 'ALL' ? '_' + groupId : ''}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Lỗi khi xuất file Excel:", error);
        alert("Lỗi: " + error.message);
    }
};

export const apiExportPayrolls = async (yearMonth, groupId) => {
    try {
        const response = await fetch(`${API_URL}/api/admin/export-payrolls/${yearMonth}?groupId=${groupId}`, {
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: `Lỗi ${response.status}: Không thể xuất file.` }));
            throw new Error(errorData.message || 'Có lỗi xảy ra.');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `BangLuong_${yearMonth}${groupId && groupId !== 'ALL' ? '_' + groupId : ''}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Lỗi khi xuất file bảng lương:", error);
        alert("Lỗi khi xuất file: " + error.message);
    }
};
// Hàm mới để xuất file chấm công theo tháng và nhóm
// CẬP NHẬT: Thêm biến limit16h vào Xuất Công Tổng Hợp
export const apiExportTimesheet = async (yearMonth, groupId, limit16h = false) => {
    try {
        const response = await fetch(`${API_URL}/api/admin/export-timesheet/${yearMonth}?groupId=${groupId}&limit16h=${limit16h}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => { return { message: `Lỗi ${response.status}: Không thể xuất file.` }; });
            throw new Error(errorData.message || 'Có lỗi xảy ra.');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `BaoCaoChamCong_${limit16h?'16h_':''}${yearMonth}${groupId && groupId !== 'ALL' ? '_' + groupId : ''}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);

    } catch (error) {
        alert("Lỗi khi xuất file: " + error.message);
        if (error.message.includes('Chưa đăng nhập') || error.message.includes('401')) { window.location.reload(); }
    }
};

// CẬP NHẬT: Thêm biến limit16h vào Xuất Công Cá Nhân
export const apiExportTimesheetForEmployee = async (empid, yearMonth, limit16h = false) => {
    try {
        const response = await fetch(`${API_URL}/api/admin/export-timesheet-employee/${empid}/${yearMonth}?limit16h=${limit16h}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: `Lỗi ${response.status}: Không thể xuất file.` }));
            throw new Error(errorData.message || 'Có lỗi xảy ra.');
        }

        const blob = await response.blob();
        let filename = `ChamCong_${limit16h?'16h_':''}${empid}_${yearMonth}.xlsx`;
        const disposition = response.headers.get('Content-Disposition');
        if (disposition && disposition.indexOf('filename=') !== -1) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
            if (matches != null && matches[1]) { filename = decodeURIComponent(matches[1].replace(/['"]/g, '')); }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (error) {
        throw error;
    }
};
// --- TIMESHEET APIs ---
export const apiGetMonthlyTimesheetSummary = (yymm, groupId = 'ALL') => customFetch(`/api/admin/timesheet/summary/${yymm}?groupId=${groupId}`);
export const apiGetTimesheetForEmployee = (empid, yymm) => customFetch(`/api/admin/timesheet/${empid}/${yymm}`);
export const apiUpdateTimesheetEntry = (autoid, data) => customFetch(`/api/admin/timesheet/${autoid}`, {
    method: 'PUT',
    body: data
});
export const apiUploadTimesheet = (data) => customFetch('/api/admin/timesheet/upload', {
    method: 'POST',
    body: data
});
export const apiGetAllEmployeesDetails = () => customFetch('/api/admin/timesheet/groupids');


// === OTHER DEDUCTIONS APIs ===
export const apiAddOtherDeduction = (data) => customFetch('/api/admin/deduction', { method: 'POST', body: data });
export const apiGetOtherDeductions = (month, year) => customFetch(`/api/admin/deduction?month=${month}&year=${year}`);
export const apiDeleteOtherDeduction = (id) => customFetch(`/api/admin/deduction/${id}`, { method: 'DELETE' });

// --- Basic Salary Module APIs ---
export const apiGetEmployeesByGroup = (groupid, month) => customFetch(`/api/salary-module/employee/by-group?groupid=${groupid}&month=${month}`);
export const apiGetBasicSalaryData = (filters) => customFetch(`/api/salary-module/salary/basic`, { method: 'POST', body: filters });
export const apiSaveBasicSalaryData = (payload) => customFetch(`/api/salary-module/salary/basic/save`, { method: 'POST', body: payload });

// New APIs for dropdowns
export const apiGetSalaryCodes = () => customFetch(`/api/salary-module/salary-codes`);
export const apiGetJobCodes = () => customFetch(`/api/salary-module/job-codes`);


// --- Insurance Module APIs ---
export const apiGetInsuranceSettings = (yymm, whsno) => customFetch(`/api/salary-module/insurance/settings?yymm=${yymm}&whsno=${whsno}`);
export const apiSaveInsuranceSettings = (settingsData) => customFetch(`/api/salary-module/insurance/settings`, { method: 'POST', body: settingsData });
export const apiCalculateInsurance = (filterData) => customFetch(`/api/salary-module/insurance/calculate`, { method: 'POST', body: filterData });
export const apiConfirmInsurance = (confirmationData) => customFetch(`/api/salary-module/insurance/confirm`, { method: 'POST', body: confirmationData });

// --- Salary Calculation APIs ---
export const apiPreviewOvertimePay = (payload) => customFetch(`/api/salary/preview/overtime`, { method: 'POST', body: payload });
export const apiConfirmOvertimePay = (payload) => customFetch(`/api/salary/confirm/overtime`, { method: 'POST', body: payload });

// --- Admin - EMPNoTax APIs ---
export const apiGetAllEmpNoTax = () => customFetch('/api/admin/empnotax');
export const apiGetEmpNoTaxByEmpId = (empid) => customFetch(`/api/admin/empnotax/${empid}`);
export const apiConfirmEmpNoTax = (payload) => customFetch('/api/admin/empnotax/confirm', { method: 'POST', body: payload });
// --- Salary Calculation (YECE12) APIs ---
export const apiGetRawSalaryData = (payload) => customFetch('/api/salary/calculate/raw-data', { method: 'POST', body: payload });
export const apiConfirmSalary = (payload) => customFetch('/api/salary/calculate/confirm', { method: 'POST', body: payload });
// --- Export Salary to Excel ---
// CẬP NHẬT: Thêm biến limit16h vào Xuất Lương
export const apiExportSalaryExcel = (filters) => {
    const params = new URLSearchParams({
        yymm: filters.yymm.replace('-', ''),
        groupid: filters.groupid || 'ALL',
        empid: filters.empid || 'ALL',
        limit16h: filters.limit16h || false
    });
    return apiFetchFile(`/api/export-salary?${params.toString()}`);
};
// --- MODULE QUẢN LÝ BASICCODE (ĐÃ TÁCH RIÊNG) ---
export const apiGetAllBasicCodeFuncs = () => customFetch(`/api/basic-codes/all-funcs`);
export const apiGetBasicCode = (func) => customFetch(`/api/basic-codes/${func}`);
export const apiAddBasicCode = (data) => customFetch('/api/basic-codes', { method: 'POST', body: data });
export const apiUpdateBasicCode = (id, data) => customFetch(`/api/basic-codes/${id}`, { method: 'PUT', body: data });
export const apiDeleteBasicCode = (id) => customFetch(`/api/basic-codes/${id}`, { method: 'DELETE' });
// API for EmpSalaryBasic Management
export const apiGetSalaryBasics = (filters) => {
    const params = new URLSearchParams();
    if (filters.bwhsno) params.append('bwhsno', filters.bwhsno);
    if (filters.country) params.append('country', filters.country);
    if (filters.func) params.append('func', filters.func); // Sửa: Gửi tham số func
    
    return customFetch(`/api/salary-basics?${params.toString()}`);
};
export const apiGetSalaryBasicFuncs = () => customFetch('/api/salary-basics/funcs'); // API mới
export const apiAddSalaryBasic = (data) => customFetch('/api/salary-basics', { method: 'POST', body: data });
export const apiUpdateSalaryBasic = (id, data) => customFetch(`/api/salary-basics/${id}`, { method: 'PUT', body: data });
export const apiDeleteSalaryBasic = (id) => customFetch(`/api/salary-basics/${id}`, { method: 'DELETE' });
// API thông báo
export const apiFetchUnreadNotifications = (userId) => {
    // Sửa: Dùng customFetch để tự động gửi cookie và xử lý lỗi.
    return customFetch(`/api/notifications/unread/${userId}`);
};

export const apiMarkNotificationAsRead = (userId, notificationId) => {
    // Sửa: Dùng customFetch.
    return customFetch(`/api/notifications/mark-as-read`, {
        method: 'POST',
        body: { userId, notificationId }, // customFetch sẽ tự động chuyển thành JSON
    });
};

// Cập nhật hàm create để gửi thêm 'sender'
export const apiCreateNotification = (title, content, sender) => {
    return customFetch(`/api/notifications`, {
        method: 'POST',
        body: { title, content, sender },
    });
};

export const apiGetAllNotifications = () => {
    // Lấy tất cả thông báo để quản lý
    return customFetch(`/api/notifications/all`);
};

export const apiDeleteNotification = (notificationId) => {
    // Xóa một thông báo
    return customFetch(`/api/notifications/${notificationId}`, {
        method: 'DELETE',
    });
};
export const apiUpdateNotification = (notificationId, title, content, sender) => {
    return customFetch(`/api/notifications/${notificationId}`, {
        method: 'PUT',
        body: { title, content, sender },
    });
};
export const apiFetchNotificationHistory = (userId) => {
    return customFetch(`/api/notifications/history/${userId}`);
};
// * Gọi API để chốt sổ MIS cho một tháng cụ thể.
export const apiCloseSalaryMonth = (payload) => {
    // Logic đã được đơn giản hóa: chỉ gửi tháng cần sao lưu.
    return customFetch('/api/salary/salaries/close-mis', {
        method: 'POST',
        body: payload,
    });
};

//* Lấy dữ liệu lương tháng 13 theo năm
export const apiAdminGetLuongT13ByYear = (year) => customFetch(`/api/admin/luong-t13/${year}`);
// --- Zalo Management APIs ---
export const apiGetAllZaloMappings = () => customFetch('/api/admin/employee/zalo/all'); // Thêm dòng này
export const apiGetZaloMapping = (empid) => customFetch(`/api/admin/employee/${empid}/zalo`);
export const apiUpdateZaloMapping = (empid, data) => customFetch(`/api/admin/employee/${empid}/zalo`, { method: 'PUT', body: data });
// THÊM DÒNG NÀY ĐỂ FRONTEND CÓ THỂ GỌI LỆNH GỬI LƯƠNG:
export const apiSendSalaryZalo = (payload) => customFetch('/api/zalo/send-salary', { method: 'POST', body: payload });
// --- Admin Dashboard APIs ---
export const apiGetDashboardOverview = () => customFetch('/api/admin/dashboard/overview');
// --- Mapping Mã Chấm Công APIs ---
export const apiGetAllMappings = () => customFetch('/api/mapping');
export const apiSaveMapping = (mappingData) => customFetch('/api/mapping', { method: 'POST', body: mappingData });